/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconSparklesSolid from '@apollo/icons/default/IconSparklesSolid.svg';
import { Button, ButtonGroup, Spinner } from '@apollo/orbit';
import { jsx } from '@emotion/react';
import { useEffect, useRef } from 'react';
import { Redirect, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { SelectedOperation } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/OperationsList';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Loading } from 'src/components/common/loading/Loading';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { OperationInsightsFilter } from 'src/components/metricsFilter/OperationInsightsFilter';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  TimeRangeErrorCode,
  resetTimeRangeLocation,
  useSelectedTimeRange,
} from 'src/hooks/useSelectedTimeRange';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { useLDFlag } from 'src/lib/launchDarkly';
import { isSubscription } from 'src/lib/metrics';
import { mergeQueryParams, setOverlayVisibleLocation } from 'src/lib/routing';

import { OperationTypeBadge } from '../../../components/common/operationTypeBadge/OperationTypeBadge';
import { ServiceRangeUnavailable } from '../errors/serviceRangeUnavailable/ServiceRangeUnavailable';
import { insightsRouteConfig } from '../routes';

import { AllowedTabsProvider } from './allowedTabsProvider/AllowedTabsProvider';
import { useInsightsL3NavState } from './insightsL3Nav/useInsightsL3NavState';
import { OperationDetailsDrawer } from './operationDetails/OperationDetailsDrawer';
import { OperationIdSection } from './OperationIdSection';
import { ServiceReportPane } from './report-pane/serviceReportPane/ServiceReportPane';
import { SaveCollectionEntryFromOperationsPaneModal } from './saveCollectionEntryFromOperationsPaneModal/SaveCollectionEntryFromOperationsPaneModal';
import { SetupOperationMetrics } from './SetupOperationMetrics';

interface OperationsMainContentProps {
  serviceId: string | undefined;
  accountId: string | undefined;
  operationSignature: string | undefined;
  selectedOperation?: SelectedOperation;
  reportingEnabled?: boolean;
  isSupergraphVariant: boolean;
}

export const OperationsMainContent = ({
  serviceId,
  accountId,
  operationSignature,
  selectedOperation,
  reportingEnabled,
  isSupergraphVariant,
}: OperationsMainContentProps) => {
  const location = useLocation();
  const { isOpen, onOpen } = useInsightsL3NavState();
  const trackCustomEvent = useTrackCustomEvent();

  const {
    graphId,
    graphVariant = Config.service.defaultVariant,
    tab,
    fastModeRange,
    query: operationId,
    queryName,
    overlay,
    insightsTab,
  } = useRouteParams(insightsRouteConfig);

  const errorTabRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const curRef = errorTabRef.current;

    const handleClick = () => {
      if (tab !== 'errors') {
        trackCustomEvent({
          category: 'Operation Insights',
          action: 'header_error_tab_click',
        });
      }
    };
    if (curRef !== null) {
      curRef.addEventListener('click', handleClick);
    }

    return () => curRef?.removeEventListener('click', handleClick);
  }, [errorTabRef, tab, trackCustomEvent]);

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const {
    timeRangeInfo,
    loading: timeRangeLoading,
    error: timeRangeError,
  } = useSelectedTimeRange();
  const showErrorCategorization =
    useLDFlag('insights-error-categorization-ui') && isSupergraphVariant;

  if (!serviceId) {
    return null;
  }

  if (timeRangeError?.code === TimeRangeErrorCode.INVALID) {
    return <Redirect to={resetTimeRangeLocation(location)} />;
  }

  // Since operations are the only insights L3 option which has an overview
  // page, we render it when first selecting non-operation options until the
  // respective list query has resolved and we can auto-select the first row.
  // While that list query is loading we need to handle the loading state here
  // until the list component has either redirected to the field's route or a
  // "not found" route. In the future, we could simplify this by consolidating
  // the insights pages and lifting the list queries up the component hierarchy
  // so state can be easily passed down.
  if (insightsTab !== 'operations') {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const canUseTraces = !!currentPlan.currentPlan?.traces;
  const title = selectedOperation?.name ?? 'Insights';
  const sigIsSubscription = operationSignature
    ? isSubscription(operationSignature)
    : false;

  const highlightNewFeatureTabs = showErrorCategorization ? ['errors'] : [];

  return (
    <AllowedTabsProvider isDemoPage={!accountId}>
      {({ clickableTabs, visibleTabs, defaultTab }) => {
        const activeTab = tab || defaultTab.value;
        return (
          <div className="flex flex-1 flex-col bg-primary">
            <InsightsEnforcementWarning />
            <div className="sticky top-0 z-20 border-b border-primary bg-primary px-8 pt-4">
              <PageTitle
                className="border-b-0 px-[0px]"
                title={title}
                leftAdornment={
                  !isOpen ? (
                    <Tooltip label="Expand">
                      <InsightsExpandButton onOpen={onOpen} />
                    </Tooltip>
                  ) : null
                }
                rightSide={
                  <div className="flex items-center">
                    <ButtonGroup>
                      {selectedOperation && (
                        <Button
                          as={Link}
                          to={setOverlayVisibleLocation({
                            location,
                            overlayName: 'operation-details',
                          })}
                        >
                          View operation
                        </Button>
                      )}
                      {reportingEnabled && timeRangeInfo && (
                        <OperationInsightsFilter
                          selectedRange={timeRangeInfo.selectedRange}
                          maxRangeInDays={
                            timeRangeInfo.accountCapabilities.maxRangeInDays
                          }
                          ranges={
                            timeRangeInfo.accountCapabilities
                              .ranges as ReadonlyArray<TimeRangeOption>
                          }
                          fastModeRange={fastModeRange}
                        />
                      )}
                    </ButtonGroup>
                  </div>
                }
              >
                <div className="flex w-full flex-row items-center">
                  <div className="truncate pr-2">{title}</div>
                  <OperationTypeBadge
                    signature={operationSignature}
                    size="lg"
                  />
                </div>
                {operationId && (
                  <OperationIdSection operationId={operationId} />
                )}
              </PageTitle>
              {reportingEnabled && (
                <div>
                  {visibleTabs.map((tabConfig) => {
                    const disabled = !clickableTabs.find(
                      (clickableTab) => clickableTab.value === tabConfig.value,
                    );
                    const maybeRefProp =
                      tabConfig.title === 'Errors' ? { ref: errorTabRef } : {};
                    return (
                      <HeaderTab
                        key={tabConfig.value}
                        isActive={() => activeTab === tabConfig.value}
                        tabID={tabConfig.value}
                        badge={
                          highlightNewFeatureTabs.includes(tabConfig.value) ? (
                            <Tooltip label="This page has new features to explore">
                              <IconSparklesSolid className="icon-beta ml-0.5 opacity-60" />
                            </Tooltip>
                          ) : null
                        }
                        to={{
                          ...location,
                          search: mergeQueryParams(location.search, {
                            tab: tabConfig.value,
                          }),
                        }}
                        tabName={
                          disabled ? (
                            <Tooltip
                              label={`Select an individual operation to view the ${tabConfig.title} tab.`}
                            >
                              <span>{tabConfig.title}</span>
                            </Tooltip>
                          ) : (
                            tabConfig.title
                          )
                        }
                        disabled={disabled}
                        {...maybeRefProp}
                      />
                    );
                  })}
                </div>
              )}
            </div>
            <div className="relative size-full px-8 pt-6">
              <div className="flex justify-center">
                {!reportingEnabled ? (
                  <SetupOperationMetrics />
                ) : timeRangeLoading ? (
                  <Loading />
                ) : !timeRangeInfo ? (
                  <ServiceRangeUnavailable />
                ) : (
                  <ServiceReportPane
                    isTracingAvailable={canUseTraces}
                    serviceId={serviceId}
                    schemaTag={graphVariant}
                    selectedRange={timeRangeInfo.selectedRange}
                    clickableTabs={clickableTabs}
                    defaultTab={defaultTab}
                    isSubscription={sigIsSubscription}
                    isSupergraphVariant={isSupergraphVariant}
                  />
                )}
              </div>
            </div>
            <URLModal name={Config.modals.saveMetricsOperationToCollection}>
              {({ isOpen: isModalOpen, closeModal }) => (
                <SaveCollectionEntryFromOperationsPaneModal
                  isOpen={isModalOpen}
                  onClose={closeModal}
                  query={operationId ?? ''}
                />
              )}
            </URLModal>
            <OperationDetailsDrawer
              graphId={graphId}
              graphVariant={graphVariant}
              operationId={operationId}
              queryName={queryName}
              overlay={overlay}
            />
          </div>
        );
      }}
    </AllowedTabsProvider>
  );
};
